import React, { useEffect, useId, useRef } from "react";
import ReactDOM from "react-dom";
import { FiX } from "react-icons/fi";
import { Button } from "../UI";
import styles from "./styles.module.css";
type ModalProps = {
  show: boolean;
  closing?: boolean;
  children: React.ReactNode;
  title: string;
  onClose: () => void;
  closeDisabled?: boolean;
  size?: "default" | "small";
};
export default function Modal({
  show,
  children,
  onClose,
  title,
  closeDisabled = false,
  size = "default",
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const disabledRef = useRef(closeDisabled);
  closeRef.current = onClose;
  disabledRef.current = closeDisabled;
  useEffect(() => {
    if (!show) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusable = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
        ) || [],
      );
    (
      dialogRef.current?.querySelector<HTMLElement>("input:not(:disabled)") ||
      focusable()[0] ||
      dialogRef.current
    )?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !disabledRef.current) {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key === "Tab") {
        const nodes = focusable();
        if (!nodes.length) {
          event.preventDefault();
          dialogRef.current?.focus();
          return;
        }
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [show]);
  if (!show) return null;
  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !closeDisabled) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`${styles.modal} ${size === "small" ? styles.small : ""}`}
      >
        <div className={styles.heading}>
          <h2 id={titleId}>{title}</h2>
          <Button
            variant="ghost"
            className={styles.close}
            aria-label={`Close ${title.toLowerCase()} dialog`}
            onClick={onClose}
            disabled={closeDisabled}
          >
            <FiX aria-hidden="true" />
          </Button>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.getElementById("modal-root") || document.body,
  );
}
